import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { workshopInvoiceService } from '../services/workshopInvoiceService';
import { CompanyInfo, InvoiceItem, Workshop, WorkshopParticipant } from '../types';
import {
  Receipt,
  FileText,
  Euro,
  Plus,
  Trash2,
  X,
  AlertCircle,
  CheckCircle,
  GraduationCap,
  User,
} from 'lucide-react';

interface WorkshopInvoiceGeneratorProps {
  workshop: Workshop;
  participant: WorkshopParticipant;
  onClose: () => void;
  onInvoiceCreated: () => void;
}

const WorkshopInvoiceGenerator: React.FC<WorkshopInvoiceGeneratorProps> = ({
  workshop,
  participant,
  onClose,
  onInvoiceCreated,
}) => {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isPaid, setIsPaid] = useState(participant.paid || false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  
  // Estado para información del participante (editable)
  const [participantInfo, setParticipantInfo] = useState({
    name: participant.participantName || '',
    surname: participant.participantSurname || '',
    email: participant.participantEmail || '',
    dni: participant.participantDni || '',
    address: participant.participantAddress || '',
    city: participant.participantCity || '',
    postalCode: participant.participantPostalCode || '',
    province: participant.participantProvince || '',
  });

  // Información de la empresa
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({
    userId: user?.uid || '',
    companyName: '',
    taxId: '',
    address: '',
    city: '',
    postalCode: '',
    province: '',
    country: 'ES',
    phone: '',
    email: user?.email || '',
    website: '',
    logoUrl: '',
    bankAccount: '',
    bankName: '',
    createdAt: new Date(),
    updatedAt: new Date(),
  });

  // Items de la factura (talleres exentos de IVA)
  const [items, setItems] = useState<
    Omit<InvoiceItem, 'id' | 'subtotal' | 'taxAmount' | 'total'>[]
  >([
    {
      description: `Taller formativo: ${workshop.title}`,
      quantity: 1,
      unitPrice: workshop.price,
      discount: 0,
      taxRate: 0, // Exento de IVA
    },
  ]);

  // Validación
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  useEffect(() => {
    if (user?.email) {
      setCompanyInfo(prev => ({
        ...prev,
        email: user.email || '',
      }));
    }
  }, [user?.email]);

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    // Validar información del participante
    if (!participantInfo.name.trim()) {
      errors.participantName = 'El nombre del participante es obligatorio';
    }
    if (!participantInfo.surname.trim()) {
      errors.participantSurname = 'Los apellidos del participante son obligatorios';
    }
    if (!participantInfo.email.trim()) {
      errors.participantEmail = 'El email del participante es obligatorio';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(participantInfo.email)) {
      errors.participantEmail = 'El email no es válido';
    }

    // Validar información de la empresa
    if (!companyInfo.companyName.trim()) {
      errors.companyName = 'El nombre de la empresa es obligatorio';
    }
    if (!companyInfo.taxId.trim()) {
      errors.taxId = 'El NIF/CIF es obligatorio';
    }
    if (!companyInfo.address.trim()) {
      errors.address = 'La dirección es obligatoria';
    }
    if (!companyInfo.city.trim()) {
      errors.city = 'La ciudad es obligatoria';
    }
    if (!companyInfo.postalCode.trim()) {
      errors.postalCode = 'El código postal es obligatorio';
    }
    if (!companyInfo.province.trim()) {
      errors.province = 'La provincia es obligatoria';
    }
    if (!companyInfo.phone.trim()) {
      errors.phone = 'El teléfono es obligatorio';
    }
    if (!companyInfo.email.trim()) {
      errors.email = 'El email es obligatorio';
    }

    items.forEach((item, index) => {
      if (!item.description.trim()) {
        errors[`item-${index}-description`] = 'La descripción es obligatoria';
      }
      if (item.quantity <= 0) {
        errors[`item-${index}-quantity`] = 'La cantidad debe ser mayor a 0';
      }
      if (item.unitPrice <= 0) {
        errors[`item-${index}-unitPrice`] = 'El precio unitario debe ser mayor a 0';
      }
      if (item.taxRate !== 0) {
        errors[`item-${index}-taxRate`] = 'Los talleres están exentos de IVA (0%)';
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCompanyInfoChange = (
    field: keyof CompanyInfo,
    value: string
  ) => {
    setCompanyInfo(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleItemChange = (
    index: number,
    field: keyof Omit<InvoiceItem, 'id' | 'subtotal' | 'taxAmount' | 'total'>,
    value: string | number
  ) => {
    setItems(prev => {
      const newItems = [...prev];
      newItems[index] = {
        ...newItems[index],
        [field]: field === 'taxRate' ? 0 : value, // Forzar IVA a 0
      };
      return newItems;
    });

    // Limpiar error de validación
    if (validationErrors[`item-${index}-${field}`]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`item-${index}-${field}`];
        return newErrors;
      });
    }
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        description: '',
        quantity: 1,
        unitPrice: 0,
        discount: 0,
        taxRate: 0, // Exento de IVA
      },
    ]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const createInvoice = async () => {
    if (!validateForm()) {
      return;
    }

    if (!user?.uid) {
      setError('No se pudo obtener la información del usuario');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Actualizar información del participante con los datos editados
      const updatedParticipant: WorkshopParticipant = {
        ...participant,
        participantName: participantInfo.name,
        participantSurname: participantInfo.surname,
        participantEmail: participantInfo.email,
        participantDni: participantInfo.dni || undefined,
        participantAddress: participantInfo.address || undefined,
        participantCity: participantInfo.city || undefined,
        participantPostalCode: participantInfo.postalCode || undefined,
        participantProvince: participantInfo.province || undefined,
      };

      // Crear la factura
      const invoice = await workshopInvoiceService.createWorkshopInvoice(
        user.uid,
        workshop,
        updatedParticipant,
        companyInfo,
        items,
        {
          isPaid,
          paymentMethod: isPaid ? paymentMethod : undefined,
          stripePaymentIntentId: participant.stripePaymentIntentId,
          stripeSessionId: participant.stripeSessionId,
        }
      );

      setSuccess(`✅ Factura ${invoice.invoiceNumber} creada exitosamente`);

      // Notificar al componente padre
      setTimeout(() => {
        onInvoiceCreated();
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error creando factura de taller:', error);
      setError(
        `❌ Error al crear la factura: ${
          error instanceof Error ? error.message : 'Error desconocido'
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  const calculateItemTotals = (
    item: Omit<InvoiceItem, 'id' | 'subtotal' | 'taxAmount' | 'total'>
  ) => {
    const subtotal =
      item.quantity * item.unitPrice * (1 - (item.discount || 0) / 100);
    const taxAmount = 0; // Siempre 0 para talleres
    const total = subtotal; // Total = subtotal (sin IVA)

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      taxAmount: 0,
      total: Math.round(total * 100) / 100,
    };
  };

  const calculateTotals = () => {
    const totals = items.reduce(
      (acc, item) => {
        const itemTotals = calculateItemTotals(item);
        return {
          subtotal: acc.subtotal + itemTotals.subtotal,
          taxAmount: 0, // Siempre 0
          total: acc.total + itemTotals.total,
        };
      },
      { subtotal: 0, taxAmount: 0, total: 0 }
    );

    return {
      subtotal: Math.round(totals.subtotal * 100) / 100,
      taxAmount: 0,
      total: Math.round(totals.total * 100) / 100,
    };
  };

  const totals = calculateTotals();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-pastel-blue to-pastel-blue-light p-6 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white p-2 rounded-lg">
              <GraduationCap className="h-6 w-6 text-pastel-blue" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">
                Factura de Taller Formativo
              </h2>
              <p className="text-pastel-blue-light text-sm">
                {workshop.title} - {participantInfo.name}{' '}
                {participantInfo.surname}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Información del Participante */}
          <div className="border border-pastel-gray-light rounded-xl p-4">
            <h3 className="font-semibold text-pastel-gray-dark mb-4 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Información del Participante
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={participantInfo.name}
                  onChange={e => {
                    setParticipantInfo(prev => ({ ...prev, name: e.target.value }));
                    if (validationErrors.participantName) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.participantName;
                        return newErrors;
                      });
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent ${
                    validationErrors.participantName ? 'border-red-500' : ''
                  }`}
                />
                {validationErrors.participantName && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.participantName}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  Apellidos *
                </label>
                <input
                  type="text"
                  value={participantInfo.surname}
                  onChange={e => {
                    setParticipantInfo(prev => ({ ...prev, surname: e.target.value }));
                    if (validationErrors.participantSurname) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.participantSurname;
                        return newErrors;
                      });
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent ${
                    validationErrors.participantSurname ? 'border-red-500' : ''
                  }`}
                />
                {validationErrors.participantSurname && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.participantSurname}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={participantInfo.email}
                  onChange={e => {
                    setParticipantInfo(prev => ({ ...prev, email: e.target.value }));
                    if (validationErrors.participantEmail) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.participantEmail;
                        return newErrors;
                      });
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent ${
                    validationErrors.participantEmail ? 'border-red-500' : ''
                  }`}
                />
                {validationErrors.participantEmail && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.participantEmail}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  DNI
                </label>
                <input
                  type="text"
                  value={participantInfo.dni}
                  onChange={e =>
                    setParticipantInfo(prev => ({ ...prev, dni: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  Dirección
                </label>
                <input
                  type="text"
                  value={participantInfo.address}
                  onChange={e =>
                    setParticipantInfo(prev => ({ ...prev, address: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  Ciudad
                </label>
                <input
                  type="text"
                  value={participantInfo.city}
                  onChange={e =>
                    setParticipantInfo(prev => ({ ...prev, city: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  Código Postal
                </label>
                <input
                  type="text"
                  value={participantInfo.postalCode}
                  onChange={e =>
                    setParticipantInfo(prev => ({ ...prev, postalCode: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  Provincia
                </label>
                <input
                  type="text"
                  value={participantInfo.province}
                  onChange={e =>
                    setParticipantInfo(prev => ({ ...prev, province: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Información de la Empresa */}
          <div className="border border-pastel-gray-light rounded-xl p-4">
            <h3 className="font-semibold text-pastel-gray-dark mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Información de la Empresa
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  Nombre de la Empresa *
                </label>
                <input
                  type="text"
                  value={companyInfo.companyName}
                  onChange={e =>
                    handleCompanyInfoChange('companyName', e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent ${
                    validationErrors.companyName ? 'border-red-500' : ''
                  }`}
                />
                {validationErrors.companyName && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.companyName}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  NIF/CIF *
                </label>
                <input
                  type="text"
                  value={companyInfo.taxId}
                  onChange={e => handleCompanyInfoChange('taxId', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent ${
                    validationErrors.taxId ? 'border-red-500' : ''
                  }`}
                />
                {validationErrors.taxId && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.taxId}
                  </p>
                )}
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  Dirección *
                </label>
                <input
                  type="text"
                  value={companyInfo.address}
                  onChange={e =>
                    handleCompanyInfoChange('address', e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent ${
                    validationErrors.address ? 'border-red-500' : ''
                  }`}
                />
                {validationErrors.address && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.address}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  Ciudad *
                </label>
                <input
                  type="text"
                  value={companyInfo.city}
                  onChange={e => handleCompanyInfoChange('city', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent ${
                    validationErrors.city ? 'border-red-500' : ''
                  }`}
                />
                {validationErrors.city && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.city}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  Código Postal *
                </label>
                <input
                  type="text"
                  value={companyInfo.postalCode}
                  onChange={e =>
                    handleCompanyInfoChange('postalCode', e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent ${
                    validationErrors.postalCode ? 'border-red-500' : ''
                  }`}
                />
                {validationErrors.postalCode && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.postalCode}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  Provincia *
                </label>
                <input
                  type="text"
                  value={companyInfo.province}
                  onChange={e =>
                    handleCompanyInfoChange('province', e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent ${
                    validationErrors.province ? 'border-red-500' : ''
                  }`}
                />
                {validationErrors.province && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.province}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  Teléfono *
                </label>
                <input
                  type="text"
                  value={companyInfo.phone}
                  onChange={e => handleCompanyInfoChange('phone', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent ${
                    validationErrors.phone ? 'border-red-500' : ''
                  }`}
                />
                {validationErrors.phone && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.phone}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={companyInfo.email}
                  onChange={e => handleCompanyInfoChange('email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent ${
                    validationErrors.email ? 'border-red-500' : ''
                  }`}
                />
                {validationErrors.email && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.email}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Items de la Factura */}
          <div className="border border-pastel-gray-light rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-pastel-gray-dark flex items-center">
                <Receipt className="h-5 w-5 mr-2" />
                Items de la Factura (Exentos de IVA)
              </h3>
              <button
                onClick={addItem}
                className="flex items-center space-x-2 px-3 py-2 bg-pastel-blue text-white rounded-lg hover:bg-pastel-blue-dark transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Añadir Item</span>
              </button>
            </div>

            <div className="space-y-4">
              {items.map((item, index) => {
                const itemTotals = calculateItemTotals(item);
                return (
                  <div
                    key={index}
                    className="border border-pastel-gray-light rounded-lg p-4"
                  >
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-5">
                        <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                          Descripción *
                        </label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={e =>
                            handleItemChange(index, 'description', e.target.value)
                          }
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent ${
                            validationErrors[`item-${index}-description`]
                              ? 'border-red-500'
                              : ''
                          }`}
                        />
                        {validationErrors[`item-${index}-description`] && (
                          <p className="text-red-500 text-xs mt-1">
                            {validationErrors[`item-${index}-description`]}
                          </p>
                        )}
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                          Cantidad
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={e =>
                            handleItemChange(
                              index,
                              'quantity',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent ${
                            validationErrors[`item-${index}-quantity`]
                              ? 'border-red-500'
                              : ''
                          }`}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                          Precio Unit.
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={e =>
                            handleItemChange(
                              index,
                              'unitPrice',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent ${
                            validationErrors[`item-${index}-unitPrice`]
                              ? 'border-red-500'
                              : ''
                          }`}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                          Descuento %
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={item.discount || 0}
                          onChange={e =>
                            handleItemChange(
                              index,
                              'discount',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent"
                        />
                      </div>
                      <div className="col-span-1 flex items-end">
                        {items.length > 1 && (
                          <button
                            onClick={() => removeItem(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-pastel-gray-dark">
                      <span className="font-medium">IVA:</span> 0% (Exento) |{' '}
                      <span className="font-medium">Subtotal:</span>{' '}
                      {itemTotals.subtotal.toFixed(2)} € |{' '}
                      <span className="font-medium">Total:</span>{' '}
                      {itemTotals.total.toFixed(2)} €
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Totales */}
          <div className="bg-pastel-gray-light rounded-xl p-4">
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-pastel-gray-dark">
                  <span>Subtotal:</span>
                  <span className="font-medium">{totals.subtotal.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-pastel-gray-dark">
                  <span>IVA (0% - Exento):</span>
                  <span className="font-medium">{totals.taxAmount.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-pastel-gray-dark border-t border-pastel-gray-dark pt-2">
                  <span>Total:</span>
                  <span className="text-pastel-blue">
                    {totals.total.toFixed(2)} €
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Estado de Pago */}
          <div className="border border-pastel-gray-light rounded-xl p-4">
            <h3 className="font-semibold text-pastel-gray-dark mb-4">
              Estado de Pago
            </h3>
            <div className="space-y-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={isPaid}
                  onChange={e => setIsPaid(e.target.checked)}
                  className="w-4 h-4 text-pastel-blue focus:ring-pastel-blue border-pastel-gray-dark rounded"
                />
                <span className="text-pastel-gray-dark">Factura pagada</span>
              </label>
              {isPaid && (
                <div>
                  <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                    Método de Pago
                  </label>
                  <select
                    value={paymentMethod}
                    onChange={e => setPaymentMethod(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent"
                  >
                    <option value="stripe">Stripe</option>
                    <option value="transferencia">Transferencia Bancaria</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Mensajes de Error y Éxito */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <p className="text-green-700">{success}</p>
            </div>
          )}

          {/* Botones de Acción */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-pastel-gray-light">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-pastel-gray-dark text-pastel-gray-dark rounded-lg hover:bg-pastel-gray-light transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={createInvoice}
              disabled={loading}
              className="px-6 py-2 bg-gradient-to-r from-pastel-blue to-pastel-blue-light text-white rounded-lg hover:from-pastel-blue-dark hover:to-pastel-blue transition-all shadow-gentle disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Creando...</span>
                </>
              ) : (
                <>
                  <Receipt className="h-4 w-4" />
                  <span>Crear Factura</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkshopInvoiceGenerator;

